import React, { PureComponent } from "react";
import { Loader } from "react-loaders";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

import OnePanelChart from "./OnePanelChart";
import Text from "./Text";
import Teams, { colorForTeamId } from "../../api/Teams";
import { fetchCompetitionMetrics } from "../../api/MetricsService";

// Y-axis tick formatter: "$1.2M" / "$45k" / "$800".
const moneyTick = value => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};

const tooltipFormatter = value => `$${Number(value || 0).toLocaleString("en-US")}`;

// Aggregate the metricsReports.promos rows into one row per marketing
// client with a per-team spend column. Sums across all products a team
// promoted on that channel.
const buildChartData = (metricsReports, teams) => {
  const clientById = {};
  metricsReports.forEach(report => {
    (report.promos || []).forEach(p => {
      const id = p.marketing_client_id;
      if (id != null && !clientById[id]) {
        clientById[id] = p.marketing_client_name || `Client ${id}`;
      }
    });
  });

  const clientIds = Object.keys(clientById);
  return clientIds.map(idStr => {
    const id = Number(idStr);
    const row = { client: clientById[idStr] };
    teams.forEach(team => {
      const teamReport = metricsReports.find(
        r => Number(r.team) === Number(team.id)
      );
      const spend = teamReport
        ? (teamReport.promos || [])
            .filter(p => Number(p.marketing_client_id) === id)
            .reduce((s, p) => s + (Number(p.spend) || 0), 0)
        : 0;
      row[team.name] = spend;
    });
    return row;
  });
};

// Marketing Channel Spend runs its own dedicated MetricsReport fetch
// with include=product_awareness so per-team promos[] arrays are
// present. The shared Analysis fetch is left include-free because
// the backend's product_awareness include reshapes the response and
// drops unrelated fields (e.g. Available For Sale, which broke
// Inventory Management).
class MarketingChannelSpend extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      reports: null,
      loading: true,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.fetch();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.roundId !== this.props.roundId) {
      this.fetch();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  fetch = () => {
    const { roundId } = this.props;
    if (!roundId) return;
    const currentTeam = Teams.currentTeam();
    const competitionId = currentTeam ? Number(currentTeam.competition) : null;
    fetchCompetitionMetrics(competitionId || null, roundId, { include: 'product_awareness' })
      .then(data => {
        if (this._mounted) this.setState({ reports: data, loading: false });
      })
      .catch(() => {
        if (this._mounted) this.setState({ loading: false });
      });
  };

  render() {
    const { teams, teamId } = this.props;
    const { reports, loading } = this.state;

    if (loading && !reports) {
      return <Loader type="ball-scale-ripple" active />;
    }

    const safeTeams = teams || [];
    const chartData = buildChartData(reports || [], safeTeams);
    const currentTeam = safeTeams.find(t => Number(t.id) === Number(teamId));

    const chart = (
      <ResponsiveContainer width="100%" height={360}>
        <BarChart
          data={chartData}
          layout="vertical"
          barSize={18}
          margin={{ top: 12, right: 16, left: 4, bottom: 12 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={moneyTick}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            type="category"
            dataKey="client"
            tick={{ fontSize: 12 }}
            width={100}
            interval={0}
          />
          <Tooltip formatter={tooltipFormatter} />
          {safeTeams.map(team => (
            <Bar
              key={team.id}
              dataKey={team.name}
              stackId="channels"
              fill={colorForTeamId(safeTeams, team.id)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );

    return (
      <OnePanelChart
        title="Marketing Channel Spend"
        chartTitle="MARKETING CHANNEL SPEND"
        customChart={chart}
        teams={safeTeams}
      >
        <Text>
          The chart on the right reflects the channels that teams are spending on to
          create awareness of their products. Each horizontal bar is one channel and the
          stacked segments show how much each team allocated to it this round.
        </Text>
        {currentTeam ? (
          <Text>
            Find <b>{currentTeam.name}</b> in the legend to see how your allocation
            compares to the rest of the competition.
          </Text>
        ) : null}
      </OnePanelChart>
    );
  }
}

export default MarketingChannelSpend;



// WEBPACK FOOTER //
// ./src/components/analysis/MarketingChannelSpend.js